/**
 * Inline AI helpers for the campaign wizard W2 step:
 *   - <GenerateDescriptionButton /> — fills `sender_description` from the
 *     sender website using POST /v1/llm/generate-company-description.
 *   - <SuggestOffersPanel />        — proposes N offer angles from the
 *     description (and optionally website) using POST /v1/llm/suggest-offers,
 *     and applies the picked one back into `offer_title` + `offer_description`.
 *
 * Both surface upstream errors (missing OpenAI key, fetch failure, LLM
 * malformed response) inline rather than via toasts — easier to debug
 * during the rollout.
 */

import { useState } from 'react';
import { Sparkles, Loader2, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';

const W2_ERROR_CODES = {
  ai_not_configured: "L'IA n'est pas configurée (OPENAI_API_KEY manquante).",
  website_fetch_failed: 'Impossible de récupérer le site (404, blocage anti-bot, …).',
  llm_upstream_failed: 'Le LLM est indisponible — réessaye dans quelques secondes.',
  ERR_VALIDATION: 'Champs invalides — vérifie URL et description.',
};

// ── Generate company description ─────────────────────────────────────────────

interface GenerateDescriptionButtonProps {
  websiteUrl: string;
  companyName: string;
  language: string;
  onGenerated: (description: string) => void;
}

export function GenerateDescriptionButton({
  websiteUrl,
  companyName,
  language,
  onGenerated,
}: GenerateDescriptionButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const disabled = !websiteUrl.trim() || loading;

  async function run() {
    setLoading(true);
    setError(null);
    setWarnings([]);
    try {
      const { data } = await apiClient.post<{
        description: string;
        warnings?: string[];
        source?: { url: string; title: string };
      }>('/v1/llm/generate-company-description', {
        websiteUrl,
        companyName: companyName || null,
        language,
      });
      if (data.description) {
        onGenerated(data.description);
        setWarnings(data.warnings ?? []);
      } else {
        setError('Le site est trop vague pour générer une description.');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, W2_ERROR_CODES));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-start justify-between gap-3" data-testid="generate-description-row">
      <div className="text-xs text-gray-500 leading-relaxed">
        Générer la description automatiquement à partir du site web ci-dessus
        {!websiteUrl && (
          <span className="ml-1 text-amber-600">(saisis d'abord une URL valide)</span>
        )}
        {error && (
          <span className="block mt-1 text-red-600 flex items-start gap-1">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {error}
          </span>
        )}
        {warnings.length > 0 && (
          <span className="block mt-1 text-amber-600">{warnings.join(' • ')}</span>
        )}
      </div>
      <Button
        type="button"
        variant="secondary"
        onClick={run}
        disabled={disabled}
        data-testid="generate-description-btn"
      >
        {loading ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Génération…
          </>
        ) : (
          <>
            <Sparkles size={14} />
            Générer depuis le site
          </>
        )}
      </Button>
    </div>
  );
}

// ── Suggest offers ───────────────────────────────────────────────────────────

interface OfferSuggestion {
  title: string;
  description: string;
  value_prop: string;
  rationale: string;
}

interface SuggestOffersPanelProps {
  companyName: string;
  companyDescription: string;
  websiteUrl: string;
  language: string;
  onPick: (offer: OfferSuggestion) => void;
}

export function SuggestOffersPanel({
  companyName,
  companyDescription,
  websiteUrl,
  language,
  onPick,
}: SuggestOffersPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<OfferSuggestion[]>([]);
  const [pickedTitle, setPickedTitle] = useState<string | null>(null);

  const canRun = Boolean(companyDescription.trim() || websiteUrl.trim()) && !loading;

  async function run() {
    setLoading(true);
    setError(null);
    setPickedTitle(null);
    try {
      const { data } = await apiClient.post<{ offers: OfferSuggestion[] }>(
        '/v1/llm/suggest-offers',
        {
          companyName: companyName || null,
          companyDescription: companyDescription || null,
          websiteUrl: websiteUrl || null,
          language,
          count: 4,
        },
      );
      setOffers(data.offers ?? []);
    } catch (err) {
      setError(getApiErrorMessage(err, W2_ERROR_CODES));
    } finally {
      setLoading(false);
    }
  }

  function pick(offer: OfferSuggestion) {
    setPickedTitle(offer.title);
    onPick(offer);
  }

  return (
    <div
      className="rounded-md border border-dashed border-brand-primary/40 bg-brand-primary/5 p-3 space-y-2"
      data-testid="suggest-offers-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-600">
          <span className="font-medium text-brand-dark">Suggestions IA</span> — propose 3-4 angles
          d'offre à partir de la description et/ou du site web
          {!canRun && !loading && (
            <span className="ml-1 text-amber-600">
              (renseigne au moins la description ou le site)
            </span>
          )}
          {error && (
            <span className="block mt-1 text-red-600 flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {error}
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={run}
          disabled={!canRun}
          data-testid="suggest-offers-btn"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Recherche…
            </>
          ) : (
            <>
              <Sparkles size={14} />
              {offers.length > 0 ? 'Régénérer' : 'Suggérer des offres'}
            </>
          )}
        </Button>
      </div>

      {offers.length > 0 && (
        <ul className="space-y-2" data-testid="suggest-offers-list">
          {offers.map((o) => {
            const isPicked = pickedTitle === o.title;
            return (
              <li
                key={o.title}
                className={`rounded-md border bg-white p-3 transition-colors ${
                  isPicked ? 'border-brand-primary' : 'border-gray-200 hover:border-brand-primary/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-brand-dark">{o.title}</h4>
                    <p className="text-xs text-gray-700">{o.description}</p>
                    {o.value_prop && (
                      <p className="text-xs text-gray-600 italic">→ {o.value_prop}</p>
                    )}
                    {o.rationale && (
                      <p className="text-[11px] text-gray-400">Pourquoi : {o.rationale}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant={isPicked ? 'primary' : 'secondary'}
                    onClick={() => pick(o)}
                    data-testid={`suggest-offer-pick-${o.title.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {isPicked ? (
                      <>
                        <Check size={14} />
                        Appliquée
                      </>
                    ) : (
                      'Choisir'
                    )}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
